import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  Briefcase, CalendarCheck, AlertTriangle, TrendingUp, MapPin, User, FileText, Download, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const downloadPdf = async (url, filename) => {
  const res = await axios.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

const LEVEL_COLORS = ['#d64545', '#e07b39', '#e8a13a', '#e6c34a', '#b9c94a', '#8fbf4f', '#5aab3d'];
export const scoreColor = (v) => (v == null ? '#9ca3af' : LEVEL_COLORS[Math.max(0, Math.min(6, Math.round(v)))]);
const LEVEL_TEXT = ['#b02a2a', '#b5571c', '#9a6a12', '#7d6c0e', '#5d6b10', '#4a7a1f', '#3d7a2a'];
export const scoreTextColor = (v) => (v == null ? '#5C5C5C' : LEVEL_TEXT[Math.max(0, Math.min(6, Math.round(v)))]);

const Stat = ({ icon: Icon, label, value, color, sub }) => (
  <div className="rounded-xl border border-[#E5E0D8] bg-white p-4" data-testid={`intern-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-[#5C5C5C] text-xs font-semibold uppercase tracking-wide">
      <Icon className="w-4 h-4" style={{ color }} /> {label}
    </div>
    <div className="mt-1 text-2xl font-bold font-['Manrope']" style={{ color: color || '#1A1A1A' }}>{value}</div>
    {sub && <div className="text-xs text-[#8A8A8A] mt-0.5">{sub}</div>}
  </div>
);

export const InternDashboard = ({ internId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !internId) return;
    setLoading(true);
    setTrendView('daily');
    axios.get(`${API}/dashboard/intern/${internId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [internId, open]);

  const m = data?.metrics;
  const intern = data?.intern;
  const [trendView, setTrendView] = useState('daily');
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [sending, setSending] = useState(false);

  const sendProgress = async () => {
    if (!emailTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/dashboard/intern/${internId}/email-progress`, { to: emailTo.trim(), note: emailNote });
      toast.success(`Progress summary sent to ${emailTo.trim()}`);
      setEmailOpen(false); setEmailTo(''); setEmailNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the email');
    } finally { setSending(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf(`${API}/dashboard/intern/${internId}/pdf`, `${(intern?.name || 'intern').replace(/\s+/g, '-')}-progress.pdf`);
    } catch { toast.error('Could not generate the PDF. Please try again.'); } finally { setExporting(false); }
  };

  const weekly = (m?.weekly_trend || []).map((w) => ({ ...w, label: w.week.replace(/^\d{4}-/, '') }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="intern-dashboard-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-3">
            {intern?.photo_url
              ? <img src={intern.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" />
              : <span className="w-11 h-11 rounded-full bg-[#34657f] text-white flex items-center justify-center font-bold">{(intern?.name || '?').charAt(0)}</span>}
            <div>
              <div className="text-xl">{intern?.name || 'Intern'}</div>
              <div className="text-xs font-normal text-[#5C5C5C]">
                {data?.program?.name}{data?.program?.current_school_year ? ` · ${data.program.current_school_year}` : ''}
              </div>
            </div>
            {data && (
              <div className="ml-auto mr-6 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="intern-email-progress">
                  <Mail className="w-4 h-4 mr-1.5" />Email
                </Button>
                <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="intern-export-pdf">
                  <Download className="w-4 h-4 mr-1.5" />{exporting ? 'Preparing…' : 'Export PDF'}
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-center text-[#5C5C5C] py-10">Loading…</p>}

        {!loading && data && (
          <div className="space-y-5">
            {/* Needs attention banner */}
            {m?.needs_attention?.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FDECEA] border border-[#f3c7c0] px-4 py-3" data-testid="intern-attention-banner">
                <AlertTriangle className="w-5 h-5 text-[#d64545] shrink-0 mt-0.5" />
                <div className="text-sm text-[#8a2f26]"><strong>Needs attention:</strong> {m.needs_attention.join(' · ')}</div>
              </div>
            )}

            {/* Rotation / assignment */}
            <div className="rounded-xl border border-[#E5E0D8] bg-[#FAF8F4] p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] mb-3">
                <MapPin className="w-4 h-4 text-[#34657f]" /> Rotations
                <span className="text-xs font-normal text-[#5C5C5C]">(active: Rotation {data.program?.current_rotation})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {data.rotations?.map((r) => (
                  <div key={r.rotation}
                    className={`rounded-lg px-3 py-2 border text-sm ${r.active ? 'border-[#34657f] bg-white ring-1 ring-[#34657f]' : 'border-[#E5E0D8] bg-white'}`}
                    data-testid={`intern-rotation-${r.rotation}`}>
                    <div className="text-[11px] font-semibold uppercase text-[#8A8A8A]">Rotation {r.rotation}{r.active ? ' · now' : ''}</div>
                    <div className="text-[#1A1A1A]">{r.job_site || <span className="text-[#b0b0b0]">Not set</span>}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#5C5C5C]">
                <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Active: <strong className="text-[#1A1A1A]">{data.active_job_site?.name || '—'}</strong></span>
                {data.instructor_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> Instructor: {data.instructor_name}</span>}
                {data.trainer_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> Trainer: {data.trainer_name}</span>}
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={TrendingUp} label="Recent avg" value={m.recent_avg != null ? m.recent_avg : '—'} color={scoreTextColor(m.recent_avg)} sub="last 5 reports · 0–6" />
              <Stat icon={TrendingUp} label="Overall avg" value={m.overall_avg != null ? m.overall_avg : '—'} color={scoreTextColor(m.overall_avg)} sub="all reports" />
              <Stat icon={CalendarCheck} label="Attendance" value={m.attendance_rate != null ? `${m.attendance_rate}%` : '—'} color="#34657f" sub="present rate" />
              <Stat icon={FileText} label="Reports" value={m.report_count} color="#34657f" sub="on file" />
            </div>

            {/* Trend chart */}
            <div className="rounded-xl border border-[#E5E0D8] bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-[#1A1A1A]">Score trend</div>
                <div className="flex rounded-lg border border-[#E5E0D8] overflow-hidden text-xs">
                  <button onClick={() => setTrendView('daily')} data-testid="trend-view-daily"
                    className={`px-3 py-1 ${trendView === 'daily' ? 'bg-[#34657f] text-white' : 'text-[#5C5C5C]'}`}>Daily</button>
                  <button onClick={() => setTrendView('weekly')} data-testid="trend-view-weekly"
                    className={`px-3 py-1 ${trendView === 'weekly' ? 'bg-[#34657f] text-white' : 'text-[#5C5C5C]'}`}>Weekly</button>
                </div>
              </div>
              {trendView === 'daily' ? (
                m.trend?.filter((t) => t.avg != null).length > 1 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={m.trend.filter((t) => t.avg != null)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 6]} ticks={[0, 2, 4, 6]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <ReferenceLine y={3} stroke="#e07b39" strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="avg" stroke="#34657f" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[#8A8A8A] py-6 text-center">Not enough scored reports yet to chart a daily trend.</p>
                )
              ) : (
                weekly.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={weekly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 6]} ticks={[0, 2, 4, 6]} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <ReferenceLine y={3} stroke="#e07b39" strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="avg" stroke="#34657f" strokeWidth={2.5} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="weekly-trend-deltas">
                      {weekly.map((w) => (
                        <span key={w.week} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-[#FAF8F4] border border-[#E5E0D8]">
                          <span className="text-[#5C5C5C]">{w.label}</span>
                          <span className="font-semibold" style={{ color: scoreTextColor(w.avg) }}>{w.avg}</span>
                          {w.delta != null && (
                            <span className={w.delta > 0 ? 'text-[#3d7a2a]' : w.delta < 0 ? 'text-[#d64545]' : 'text-[#8A8A8A]'}>
                              {w.delta > 0 ? `▲ +${w.delta}` : w.delta < 0 ? `▼ ${w.delta}` : '→ 0'}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-[#8A8A8A] py-6 text-center">Not enough scored reports yet for a weekly trend.</p>
                )
              )}
            </div>

            {/* Skill averages */}
            {m.skill_averages && Object.keys(m.skill_averages).length > 0 && (
              <div className="rounded-xl border border-[#E5E0D8] bg-white p-4">
                <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Soft-skill averages</div>
                <div className="space-y-2">
                  {Object.entries(m.skill_averages).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-3" data-testid={`intern-skill-${k}`}>
                      <div className="w-40 text-sm text-[#5C5C5C] shrink-0">{v.label}</div>
                      <div className="flex-1 h-3 rounded-full bg-[#F0EDE7] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(v.avg / 6) * 100}%`, backgroundColor: scoreColor(v.avg) }} />
                      </div>
                      <div className="w-10 text-right text-sm font-semibold" style={{ color: scoreTextColor(v.avg) }}>{v.avg}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent reports */}
            <div className="rounded-xl border border-[#E5E0D8] bg-white p-4">
              <div className="text-sm font-semibold text-[#1A1A1A] mb-2">Recent reports</div>
              {data.reports?.length > 0 ? (
                <div className="divide-y divide-[#F0EDE7]">
                  {data.reports.slice(0, 8).map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2 text-sm" data-testid={`intern-report-${r.id}`}>
                      <span className="text-[#1A1A1A]">{r.report_date}</span>
                      <span className="text-[#5C5C5C]">{r.jobsite || '—'}</span>
                      {r.attendance && <Badge variant="outline" className="text-xs">{r.attendance}</Badge>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#8A8A8A] py-4 text-center">No reports filed yet.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <Dialog open={emailOpen} onOpenChange={(o) => { if (!o) setEmailOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="email-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Email progress summary</DialogTitle>
            <DialogDescription>
              Sends {intern?.name || 'this intern'}'s progress report as a PDF attachment — handy for IEP meetings. Confidential: only send to authorized recipients.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="email-to">Recipient email</Label>
              <Input id="email-to" type="email" placeholder="name@school.org" value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)} data-testid="email-progress-to" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email-note">Personal note (optional)</Label>
              <Textarea id="email-note" rows={3} placeholder="e.g. Attached is Alex's latest progress ahead of Thursday's IEP meeting."
                value={emailNote} onChange={(e) => setEmailNote(e.target.value)} data-testid="email-progress-note" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-[#34657f]" onClick={sendProgress} disabled={sending} data-testid="email-progress-send">
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default InternDashboard;
